import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "./components/GridBackground";

const SOURCES = [
  { name: "GitHub", icon: "⚙️", color: COLORS.github, initialX: 300, initialY: 250 },
  { name: "Jira", icon: "📋", color: COLORS.jira, initialX: 1620, initialY: 280 },
  { name: "Confluence", icon: "📄", color: COLORS.confluence, initialX: 350, initialY: 750 },
  { name: "Slack", icon: "💬", color: COLORS.slack, initialX: 1570, initialY: 720 },
  { name: "PDFs", icon: "📑", color: COLORS.accentOrange, initialX: 960, initialY: 200 },
  { name: "Docs", icon: "📝", color: COLORS.accentGreen, initialX: 960, initialY: 830 },
];

const CENTER_X = 960;
const CENTER_Y = 520;

export const Scene2Problem: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Phase 1 (0-80): Sources scatter with broken connections
  // Phase 2 (80-140): Sources converge toward center
  // Phase 3 (140-180): EAKC brain appears at center

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });
  const convergeProgress = interpolate(frame, [80, 130], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const brainAppear = spring({
    frame: frame - 130,
    fps,
    config: SPRING.bouncy,
  });

  // Exit
  const exitOpacity = interpolate(frame, [160, 180], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.08} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: "50%",
          transform: `translate(-50%, 0) scale(${interpolate(titleAppear, [0, 1], [0.8, 1])})`,
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 42,
            fontFamily: FONTS.heading,
            fontWeight: 700,
            color: COLORS.textPrimary,
          }}
        >
          Knowledge is{" "}
          <span style={{ color: COLORS.accentRed }}>Siloed</span>
        </div>
        <div
          style={{
            fontSize: 20,
            fontFamily: FONTS.body,
            color: COLORS.textSecondary,
            marginTop: 8,
          }}
        >
          Scattered across tools, disconnected, hard to search
        </div>
      </div>

      {/* Broken connection lines (visible in phase 1) */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: interpolate(convergeProgress, [0, 0.5], [0.3, 0], {
            extrapolateRight: "clamp",
          }),
        }}
      >
        {SOURCES.map((src, i) =>
          SOURCES.slice(i + 1).map((dst, j) => (
            <line
              key={`${i}-${j}`}
              x1={src.initialX}
              y1={src.initialY}
              x2={dst.initialX}
              y2={dst.initialY}
              stroke={COLORS.accentRed}
              strokeWidth={1}
              strokeDasharray="6 10"
              strokeDashoffset={frame * 2}
              opacity={0.4}
            />
          )),
        )}
      </svg>

      {/* Source icons */}
      {SOURCES.map((src, i) => {
        const iconAppear = spring({
          frame: frame - i * 6,
          fps,
          config: SPRING.bouncy,
        });

        // Float animation
        const floatY = Math.sin((frame + i * 20) * 0.04) * 8;

        // Converge toward center
        const targetX = CENTER_X + Math.cos((i / SOURCES.length) * Math.PI * 2) * 120;
        const targetY = CENTER_Y + Math.sin((i / SOURCES.length) * Math.PI * 2) * 100;
        const x = interpolate(convergeProgress, [0, 1], [src.initialX, targetX]);
        const y = interpolate(convergeProgress, [0, 1], [src.initialY, targetY]);

        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x,
              top: y + floatY,
              transform: `translate(-50%, -50%) scale(${interpolate(iconAppear, [0, 1], [0, 1])})`,
              opacity: interpolate(iconAppear, [0, 1], [0, 1]),
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: 20,
                background: `${COLORS.bgCard}`,
                border: `1.5px solid ${COLORS.borderLight}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 36,
                boxShadow: `0 4px 20px ${COLORS.bg}80`,
              }}
            >
              {src.icon}
            </div>
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 14,
                color: COLORS.textSecondary,
                fontWeight: 500,
              }}
            >
              {src.name}
            </span>
          </div>
        );
      })}

      {/* Central EAKC brain (phase 3) */}
      <div
        style={{
          position: "absolute",
          left: CENTER_X,
          top: CENTER_Y,
          transform: `translate(-50%, -50%) scale(${interpolate(brainAppear, [0, 1], [0, 1])})`,
          opacity: interpolate(brainAppear, [0, 1], [0, 1]),
        }}
      >
        <div
          style={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${COLORS.accentBlue}, ${COLORS.accentPurple})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 0 40px ${COLORS.accentPurple}40, 0 0 80px ${COLORS.accentBlue}20`,
          }}
        >
          <span style={{ fontSize: 48 }}>🧠</span>
        </div>
        <div
          style={{
            textAlign: "center",
            marginTop: 12,
            fontFamily: FONTS.heading,
            fontSize: 20,
            fontWeight: 700,
            color: COLORS.textPrimary,
          }}
        >
          EAKC
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
